import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { Button, Field, Panel, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { cn } from '@/shared/lib/utils';
import { useCreateProductsStation } from '@/api/generated/products-stations/products-stations';
import type { StationWriteRequest } from '@/api/generated/schemas';
import type { CodebookRef } from '@/modules/codebooks';
import { StationTypeSelectorField } from '../../../../components/StationTypeSelector';
import type { StationRef } from '../../../../productsApi';

const LABEL_W = 104;

interface NewStationFromMapFormProps {
  lat: number;
  lng: number;
  onCreated: (station: StationRef) => void;
  onCancel: () => void;
}

interface NewStationValues {
  name: string;
  code: string;
  stationType: CodebookRef | null;
  isDomestic: boolean;
}

/**
 * Mini-formulář „Nová stanice z mapy" (§7) — založí stanici ve sdíleném číselníku (s vlastními
 * souřadnicemi z kliknutí, bez nástupních míst dle §6.2) a vrátí ji k přidání jako zastávku.
 * Inline labely a sada polí kopírují identitu stanice (viz StationDetail): Název, Kód, Typ, Druh.
 */
export function NewStationFromMapForm({
  lat,
  lng,
  onCreated,
  onCancel,
}: NewStationFromMapFormProps) {
  const { t } = useTranslation('products');
  const createMutation = useCreateProductsStation();

  const form = useForm({
    defaultValues: { name: '', code: '', stationType: null, isDomestic: true } as NewStationValues,
    onSubmit: async ({ value }) => {
      if (value.stationType == null) {
        return;
      }
      const body: StationWriteRequest = {
        name: value.name.trim(),
        code: value.code.trim() ? value.code.trim() : null,
        stationTypeCbId: value.stationType.id,
        destinationId: null,
        isDomestic: value.isDomestic,
        timeZoneOffset: null,
        stationLatitude: lat,
        stationLongitude: lng,
        stationZoom: 13,
        stationDetails: [],
      };
      try {
        const newId = await createMutation.mutateAsync({ data: body });
        onCreated({
          stationId: newId,
          name: body.name,
          code: body.code,
          isDomestic: body.isDomestic,
          latitude: lat,
          longitude: lng,
        });
      } catch {
        toast({
          severity: 'error',
          title: t('stationChain.newStation.errorTitle'),
          description: t('stationChain.newStation.error'),
        });
      }
    },
  });

  return (
    <Panel variant="outline" icon="pin" title={t('stationChain.newStation.title')}>
      <div className="gap-phx-3 flex flex-col">
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('stationChain.newStation.name')}
              required
            />
          )}
        </form.Field>

        <form.Field name="code">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('stationChain.newStation.code')}
              placeholder="PHA-FLO"
            />
          )}
        </form.Field>

        <form.Field name="stationType">
          {(field) => (
            <StationTypeSelectorField
              field={field}
              mode="single"
              required
              layout="inline"
              labelWidth={LABEL_W}
              label={t('stationChain.newStation.type')}
              placeholder={t('station.type.placeholder')}
            />
          )}
        </form.Field>

        <form.Field name="isDomestic">
          {(field) => (
            <Field layout="inline" labelWidth={LABEL_W} label={t('station.kind.label')}>
              <div className="border-phx-border-2 rounded-phx inline-flex w-fit overflow-hidden border">
                <button
                  type="button"
                  onClick={() => field.handleChange(true)}
                  className={cn(
                    'px-phx-3 py-phx-1 text-phx-sm',
                    field.state.value ? 'bg-phx-primary text-white' : 'bg-phx-surface text-phx-text'
                  )}
                >
                  {t('station.kind.in')}
                </button>
                <button
                  type="button"
                  onClick={() => field.handleChange(false)}
                  className={cn(
                    'px-phx-3 py-phx-1 text-phx-sm',
                    !field.state.value
                      ? 'bg-phx-primary text-white'
                      : 'bg-phx-surface text-phx-text'
                  )}
                >
                  {t('station.kind.out')}
                </button>
              </div>
            </Field>
          )}
        </form.Field>

        <Field layout="inline" labelWidth={LABEL_W} label={t('stationChain.newStation.gps')}>
          <span className="text-phx-sm font-mono">
            {lat.toFixed(4)}, {lng.toFixed(4)}
          </span>
        </Field>

        <div className="gap-phx-2 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('stationChain.newStation.cancel')}
          </Button>
          <form.Subscribe
            selector={(state) => ({
              name: state.values.name,
              type: state.values.stationType,
            })}
          >
            {({ name, type }) => (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={name.trim().length === 0 || type == null || createMutation.isPending}
                loading={createMutation.isPending}
                onClick={() => void form.handleSubmit()}
              >
                {t('stationChain.newStation.confirm')}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>
    </Panel>
  );
}
